/**
 * Customer keys in the customer picker.
 *
 * A customer whose Microsoft 365 tenant CSG manages is keyed by its CIPP tenant id (a GUID).
 * A customer mapped in Odin-MCP without a CIPP id — CSG does not manage its M365 — is keyed
 * `odin:<Odin customer id>`. This module is the only place that knows the prefix.
 *
 * Anything that sends a tenant id to CIPP-MCP or to an agent's `tenant_id` / `cipp_tenant_id`
 * must go through `cippTenantIdOf`, which turns an `odin:` key into null.
 */

export const ODIN_KEY_PREFIX = 'odin:';

const NON_CIPP_PLATFORMS = ['sophos', 'acronis', 'autotask', 'scalepad', 'datto'];

export function odinKeyFor(row) {
  return ODIN_KEY_PREFIX + row.id;
}

export function isOdinKey(id) {
  return typeof id === 'string' && id.startsWith(ODIN_KEY_PREFIX);
}

/** The real CIPP tenant id of a picker tenant (or id), or null for an `odin:` key / nothing. */
export function cippTenantIdOf(tenantOrId) {
  const id = typeof tenantOrId === 'string' ? tenantOrId : tenantOrId?.id;
  if (!id || isOdinKey(id)) return null;
  return id;
}

export function isM365Managed(tenant) {
  return cippTenantIdOf(tenant) !== null;
}

/**
 * Whether a mapping's platform id is set. null, undefined and '' are unset; an object (Datto's
 * {organizationId, saasCustomerId}) is set when any of its fields is; anything else — Autotask's
 * numeric id, even 0 — is set.
 */
export function isPlatformIdSet(value) {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'object') return Object.values(value).some(isPlatformIdSet);
  return true;
}

/** True when any non-CIPP platform id is set. */
export function hasNonCippPlatform(platformIds) {
  if (!platformIds) return false;
  return NON_CIPP_PLATFORMS.some((p) => isPlatformIdSet(platformIds[p]));
}
